/**
 * GameHistory - manages game state history for undo functionality
 */

import { $dom } from '../utils/DOM';
import { Globals } from '../core/Globals';
import { GameCloneForDiff } from '../utils/Utilities';

declare let TheGame: any;

// Forward declarations
declare const DeepDiff: any;
declare function orderChanges(changes: any[]): any[];
declare function RoomChange(bRunTimerEvents: boolean, bRunEvents: boolean, bQuiet?: boolean): void;
declare function showImage(imageName: string | null): void;
declare function UpdateStatusBars(): void;
declare function SetPortrait(imageName: string | null): void;
declare namespace GameUI {
    function showGameElements(): void;
}

interface HistoryState {
    gameChanges?: any;
    textChild: Node | null;
    currentImage?: string;
}

export const GameHistory = {
    MAX_HISTORY_SIZE: 100,  // Erased by user settings
    states: [] as HistoryState[],
    /** Deep copy of TheGame when last state was saved */
    oldGameData: null as any,
    oldTextChild: null as Node | null,
    oldImage: null as string | null,
    enabled: false,

    reset: function (): void {
        this.states = [];
        this.oldGameData = null;
        this.oldTextChild = null;
        this.oldImage = null;
    },

    _saveOldInfo: function (gameData?: any): void {
        if (gameData === undefined) {
            // Clone current game data
            gameData = (window as any).GameCloneForDiff?.(TheGame);
        }
        this.oldGameData = gameData;
        // Save last text history element
        const mainText = document.getElementById('MainText');
        this.oldTextChild = mainText ? mainText.lastChild : null;
        // Save current media
        this.oldImage = (window as any).Globals?.currentImage || null;
    },

    pushState: function (): void {
        if (!this.enabled) return;

        const gameData = (window as any).GameCloneForDiff?.(TheGame);

        if (this.oldGameData !== null) {
            while (this.states.length >= this.MAX_HISTORY_SIZE) {
                this.states.shift();
            }

            const start2 = window.performance.now();

            // Save info to revert this state
            const state: HistoryState = {
                // Save the game data changes
                gameChanges: (window as any).DeepDiff?.diff?.(gameData, this.oldGameData),
                textChild: this.oldTextChild,
                currentImage: this.oldImage || undefined,
            };

            this.states.push(state);
        }

        this._saveOldInfo(gameData);
    },

    popState: function (): boolean {
        if (!this.canGoBack()) return false;

        const state = this.states.pop();
        if (!state) return false;

        // Remove elements after last text history element
        if (state.textChild && state.textChild.parentNode === null) {
            // Text history has been truncated too much
            $dom('#MainText').html('');
        } else if (state.textChild) {
            while (state.textChild.nextSibling) {
                state.textChild.nextSibling.remove();
            }
        }

        // Revert changes that happened after last push (because of timers)
        if(this.oldGameData !== null) {
            const gameData = (window as any).GameCloneForDiff?.(TheGame);
            const newChanges = (window as any).DeepDiff?.diff?.(gameData, this.oldGameData);
            if(newChanges !== undefined) {
                const orderedChanges = (window as any).orderChanges?.(newChanges) || newChanges;
                orderedChanges.forEach((change: any) => {
                    (window as any).DeepDiff?.applyChange?.(TheGame, true, change);
                });
            }
        }

        // Revert state changed
        if (state.gameChanges !== undefined) {
            // gameChanges can be undefined if only text changed
            const orderedChanges = (window as any).orderChanges?.(state.gameChanges) || state.gameChanges;
            orderedChanges.forEach((change: any) => {
                (window as any).DeepDiff?.applyChange?.(TheGame, true, change);
            });
        }

        (window as any).RoomChange?.(false, false, true);
        // Restore current media
        if (state.currentImage !== undefined) {
            // This must be done after the call to RoomChange() because it changes the image
            (window as any).showImage?.(state.currentImage);
        }
        (window as any).UpdateStatusBars?.();
        if (TheGame) {
            (window as any).SetPortrait?.(TheGame.Player.PlayerPortrait);
        }
        $dom("#playernamechoice").css("visibility", "hidden");
        $dom("#textactionchoice").css("visibility", "hidden");
        $dom("#textchoice").css("visibility", "hidden");
        $dom("#inputmenu").css("visibility", "hidden");
        $dom("#selectionmenu").css("visibility", "hidden");
        $dom("#genderchoice").css("visibility", "hidden");
        $dom("#cmdinputmenu").css("visibility", "hidden");
        const continueBtn = $dom("#Continue").get(0) as HTMLElement;
        if (continueBtn) {
            continueBtn.setAttribute('disabled', 'disabled');
        }
        (window as any).GameUI?.showGameElements?.();

        this._saveOldInfo();

        return true;
    },

    size: function (): number {
        return this.states.length;
    },

    canGoBack: function (): boolean {
        return this.states.length > 0;
    },

    noGoBackReason: function (): string {
        if (!this.enabled) return 'History is disabled';
        if (this.states.length == 0) return 'History is empty';
        return '';
    },
};

GameHistory.reset();

// Make available globally
(window as any).GameHistory = GameHistory;
